import React, { useEffect, useState } from "react";
import { MailQuestion, CheckCircle2, Send, X } from "lucide-react";
import { api } from "../api";
import { toast } from "sonner";

export function BookRequestModal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [format, setFormat] = useState("obojętnie");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const handler = () => { setOpen(true); setDone(false); };
    window.addEventListener("open-book-request", handler);
    return () => window.removeEventListener("open-book-request", handler);
  }, []);

  if (!open) return null;
  const close = () => setOpen(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Podaj poprawny adres email"); return; }
    if (!title.trim() && !description.trim()) { toast.error("Podaj tytuł książki lub opisz, czego szukasz"); return; }
    setSending(true);
    try {
      await api.post("/book-requests", { email, title, description, format });
      setDone(true);
    } catch {
      toast.error("Nie udało się wysłać zgłoszenia. Spróbuj ponownie.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2.5 text-sm text-slate-200 placeholder-slate-500 outline-none transition-colors focus:border-fuchsia-500";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="book-request-modal">
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={close} />
      <div className="glow-border relative w-full max-w-lg rounded-3xl p-[2px]">
        <div className="max-h-[88vh] overflow-y-auto rounded-3xl bg-slate-950/95 p-7 sm:p-9">
          <button onClick={close} data-testid="book-request-close" className="absolute right-4 top-4 rounded-full p-2 text-slate-400 transition-colors hover:text-white" aria-label="Zamknij">
            <X className="h-5 w-5" />
          </button>

          {!done ? (
            <>
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-fuchsia-500 to-cyan-500 shadow-lg shadow-fuchsia-500/30">
                <MailQuestion className="h-6 w-6 text-white" />
              </div>
              <h2 className="mt-5 font-display text-2xl font-extrabold tracking-tight">
                Nie znalazłeś książki, <span className="gradient-text">której szukasz?</span>
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Szukasz tematyki lub rodzaju książki, którego nie ma w naszej ofercie — a może nawet na rynku? Pomożemy!
                Podaj tytuł albo opisz, jaka tematyka Cię interesuje, a <span className="font-semibold text-slate-200">w ciągu kilku godzin</span> otrzymasz
                od nas ofertę na daną książkę lub propozycje tytułów z wybranej przez Ciebie tematyki.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="book-request-form">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-400">Adres email *</label>
                  <input data-testid="book-request-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="na ten adres wyślemy ofertę" className={inputCls} required />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-400">Tytuł książki (jeśli znasz)</label>
                  <input data-testid="book-request-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                    placeholder="np. „Wiedźmin: Sezon burz”" className={inputCls} />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-400">Czego szukasz? *</label>
                  <textarea data-testid="book-request-description" value={description} onChange={(e) => setDescription(e.target.value)}
                    rows={4} placeholder="Opisz tematykę, rodzaj, klimat — np. kryminał osadzony w górach, audiobook o inwestowaniu dla początkujących, poradnik o wychowaniu nastolatków..."
                    className={`${inputCls} resize-none`} />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-400">Preferowany format</label>
                  <select data-testid="book-request-format" value={format} onChange={(e) => setFormat(e.target.value)} className={inputCls}>
                    <option value="obojętnie">Obojętnie</option>
                    <option value="ebook">Ebook</option>
                    <option value="audiobook">Audiobook</option>
                  </select>
                </div>
                <button
                  data-testid="book-request-submit"
                  type="submit"
                  disabled={sending}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 py-3.5 font-display text-sm font-bold text-white shadow-lg shadow-fuchsia-500/25 transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-50"
                >
                  <Send className="h-4 w-4" /> {sending ? "Wysyłanie..." : "Wyślij zgłoszenie"}
                </button>
                <p className="text-center text-[11px] text-slate-500">Odpowiadamy zazwyczaj w ciągu kilku godzin roboczych.</p>
              </form>
            </>
          ) : (
            <div className="py-8 text-center" data-testid="book-request-success">
              <CheckCircle2 className="mx-auto h-16 w-16 text-teal-400" />
              <h2 className="mt-5 font-display text-2xl font-extrabold">Zgłoszenie wysłane!</h2>
              <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
                Dziękujemy! Nasz zespół poszuka książki lub przygotuje propozycje tytułów i odezwie się na adres
                <span className="font-semibold text-cyan-300"> {email}</span> w ciągu kilku godzin.
              </p>
              <button onClick={close} data-testid="book-request-done-close" className="mt-7 rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 px-8 py-3 font-display text-sm font-bold text-white transition-transform hover:scale-105">
                Świetnie!
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
